/**
 * Offline Sync Worker — Listens for connectivity changes and triggers sync.
 * Also schedules periodic cache eviction and queue cleanup.
 */
import { syncQueue } from './offlineQueue';
import { clearAllCache, clearStaleQueueEntries, evictStaleCache } from './offlineDb';
import { toast } from 'sonner';

let initialized = false;
const CLEANUP_INTERVAL_MS = 24 * 60 * 60 * 1000; // 24 hours
const CACHE_EVICTION_INTERVAL_MS = 6 * 60 * 60 * 1000; // 6 hours
const CLEANUP_KEY = 'bjj_last_queue_cleanup';
const EVICTION_KEY = 'bjj_last_cache_eviction';

/**
 * Initialize the offline sync listener.
 * Call once at app startup (main.tsx or App.tsx).
 */
export function initOfflineSync(): void {
  if (initialized) return;
  initialized = true;

  window.addEventListener('online', handleOnline);

  // Listen for auth signout to clear offline data
  window.addEventListener('supabase-signout', handleSignout);

  // Schedule periodic maintenance
  scheduleQueueCleanup();
  scheduleCacheEviction();

  if (import.meta.env.DEV) console.info('[OfflineSync] Initialized');
}

async function handleOnline(): Promise<void> {
  if (import.meta.env.DEV) console.info('[OfflineSync] Connection restored — syncing queue...');

  try {
    const { synced, failed } = await syncQueue();

    if (synced > 0) {
      toast.success(
        `${synced} ${synced === 1 ? 'ação sincronizada' : 'ações sincronizadas'} com sucesso!`,
        { duration: 4000 }
      );
    }

    if (failed > 0) {
      toast.error(
        `${failed} ${failed === 1 ? 'ação falhou' : 'ações falharam'} ao sincronizar. Tentaremos novamente.`,
        { duration: 5000 }
      );
    }
  } catch (err) {
    if (import.meta.env.DEV) console.error('[OfflineSync] Sync error:', err);
  }
}

async function handleSignout(): Promise<void> {
  try {
    await clearAllCache();
    if (import.meta.env.DEV) console.info('[OfflineSync] Cache cleared on signout');
  } catch (err) {
    if (import.meta.env.DEV) console.warn('[OfflineSync] Failed to clear cache:', err);
  }
}

/**
 * Schedule cleanup of old done/failed (max retries) queue entries.
 */
function scheduleQueueCleanup(): void {
  const runCleanup = async () => {
    try {
      const removed = await clearStaleQueueEntries(7 * 24 * 60 * 60 * 1000); // 7 days
      if (removed > 0 && import.meta.env.DEV) {
        console.info(`[OfflineSync] Cleaned ${removed} stale queue entries`);
      }
      try {
        localStorage.setItem(CLEANUP_KEY, String(Date.now()));
      } catch { /* ignore */ }
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[OfflineSync] Queue cleanup error:', err);
    }
  };

  runIfDue(CLEANUP_KEY, CLEANUP_INTERVAL_MS, runCleanup);
  setInterval(runCleanup, CLEANUP_INTERVAL_MS);
}

/**
 * Schedule periodic cache eviction (TTL + LRU + size cap).
 * Runs every 6 hours, or immediately on startup if due.
 */
function scheduleCacheEviction(): void {
  const runEviction = async () => {
    try {
      const stats = await evictStaleCache();
      const totalEvicted = stats.ttlEvicted + stats.lruEvicted + stats.sizeEvicted;
      if (totalEvicted > 0 && import.meta.env.DEV) {
        console.info(
          `[OfflineSync] Cache eviction: TTL=${stats.ttlEvicted}, LRU=${stats.lruEvicted}, Size=${stats.sizeEvicted}. ` +
          `Remaining: ${stats.totalRemaining} entries (${(stats.totalSizeBytes / 1024 / 1024).toFixed(1)}MB)`
        );
      }
      try {
        localStorage.setItem(EVICTION_KEY, String(Date.now()));
      } catch { /* ignore */ }
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[OfflineSync] Cache eviction error:', err);
    }
  };

  runIfDue(EVICTION_KEY, CACHE_EVICTION_INTERVAL_MS, runEviction);
  setInterval(runEviction, CACHE_EVICTION_INTERVAL_MS);
}

/** Run a task after a delay if enough time has passed since last run */
function runIfDue(storageKey: string, intervalMs: number, task: () => Promise<void>): void {
  try {
    const lastRun = Number(localStorage.getItem(storageKey) || '0');
    if (Date.now() - lastRun >= intervalMs) {
      setTimeout(() => task(), 5000); // delay to avoid blocking startup
    }
  } catch { /* ignore */ }
}

/**
 * Call this when the user signs out to clear all offline data.
 */
export function triggerOfflineClear(): void {
  window.dispatchEvent(new Event('supabase-signout'));
}
